import type { LocationQueryRaw, RouteLocationRaw } from 'vue-router'
import { getErrorMessage } from '~/utils/errors'
import {
  DEFAULT_DESTINATION_DISPLAY_MODE,
  type DestinationDisplayMode,
  isDestinationDisplayMode
} from '~/utils/destinationDisplayMode'
import {
  dateRangeModeQueryFields,
  resolveActiveDateRangeMode
} from '~/utils/dateRangeMode'

export const useDestinationStore = defineStore('selectedDestination', () => {

  // Config
  const { locale, t } = useI18n()
  const route = useRoute()
  const router = useRouter()
  const runtimeConfig = useRuntimeConfig()  // Config
  const appConfig = useAppConfig()

  // GTM
  const { proxy } = useScriptGoogleTagManager()

  // Stores
  const currencyStore = useCurrencyStore()
  const { currency } = storeToRefs(currencyStore)
  const { isHome } = useIsHome()

  // Estado reactivo
  const cookieDestination: Ref<Destination | null> = useCookie('destination', { maxAge: 86400 }) // cookie destino, expira en 24 horas
  const selectedDestination: Ref<Destination | null> = ref(cookieDestination.value ?? null)
  const geoIpDestination: Ref<Destination | null> = useCookie('geoIpDestination', { maxAge: 86400 })
  const geoIpLocation: Ref<GeoCoordinates | null> = useCookie('geoIpLocation', { maxAge: 86400 })
  /** Nombre real del destino (URL `data-search`, cookie): formularios, SEO, analytics. */
  const labelDestination: ComputedRef<string> = computed(() => route.query?.['data-search'] as string ?? cookieDestination.value?.name_translated ?? '')
  /**
   * Cómo mostrar el destino en UI (`named` = nombre real; `gps_near_me` / `map_point` = etiquetas i18n).
   * `labelDestination` sigue llevando el nombre real para URL, cookie y on-demand.
   */
  const destinationDisplayMode = useCookie<DestinationDisplayMode>('destinationDisplayMode', {
    maxAge: 86400,
    default: () => DEFAULT_DESTINATION_DISPLAY_MODE
  })

  watchEffect(() => {
    if (!isDestinationDisplayMode(destinationDisplayMode.value)) {
      destinationDisplayMode.value = DEFAULT_DESTINATION_DISPLAY_MODE
    }
  })

  const labelDestinationDisplay = computed(() => {
    const mode = destinationDisplayMode.value
    if (mode === 'gps_near_me') return t('labels.ubicacionActual')
    if (mode === 'map_point') return t('labels.zonaActual')
    return labelDestination.value
  })
  const destination_id: Ref<number | null> = ref(route.query?.['data-destinationId'] ? Number(route.query['data-destinationId']) : null)

  // Sincronizar destination_id con la URL cuando cambia data-destinationId (p. ej. desde el menú destinos)
  watch(() => route.query?.['data-destinationId'], (newId) => {
    if (newId != null && newId !== '') {
      destination_id.value = Number(newId)
    }
  }, { immediate: true })

  const distance: ComputedRef<number | null> = computed(() => selectedDestination.value?.category_id === 3 ? selectedDestination.value?.search_distance ?? 20 : 20)
  type HotelDispoMarker = { date: Date; type: 'dot'; color: string }
  const hotelDispo: Ref<HotelDispoMarker[] | null> = ref(null)

  /** Coords solo desde destination.location (cookie); data-latitude/longitude en URL se ignoran. */
  const latitude = computed(() => {
    const location = selectedDestination.value?.location?.split(',') || ['0', '0']
    return Number(location[0])
  })

  const longitude = computed(() => {
    const location = selectedDestination.value?.location?.split(',') || ['0', '0']
    return Number(location[1])
  })

  watch(selectedDestination, async (newDest, oldDest) => {

    if (newDest) {

      // ID destino
      destination_id.value = newDest?.category_id === 3 ? null : newDest?.element_id ? newDest.element_id : null

      // Cookies
      cookieDestination.value = newDest

      // Datalayers num_hotels = 0
      if (newDest?.num_hotels == 0) {
        proxy.dataLayer.push({ event: 'BusquedaSinHotelesEvent', value: newDest?.name_general })
      }

      // Actualizar rutas o redireccionamiento hotel o search (si no es home)
      if (!isHome.value) {
        const destinationChanged = newDest && oldDest && oldDest?.name_translated != newDest?.name_translated && newDest?.element_id != Number(route.query?.['data-destinationId'])
        handleDestinationRedirect(newDest, destinationChanged ?? false)
      }

      // Ver si es hotel
      const today = ref(formatDateApi(new Date(new Date().setHours(12, 0, 0, 0))))
      if (newDest?.category_id === 1) {
        try {
          const dispoData = await $fetch<HotelDaysAvailabilityResponse>(`${useBaseUrl()}/hotel-days-availability`, {
            headers: {
              "Content-Type": "application/json",
            },
            query: {
              date: today.value,
              hotel_id: newDest.id,
              channel_id: appConfig.channel_id,
              currency: currency.value,
              months: 12
            }
          })
          const packHour = dispoData?.data?.pack_hour ?? {}
          const dispoArray = Object.values(packHour as Record<string, string[]>).reduce<HotelDispoMarker[]>((acc, dates) => {
            return acc.concat(dates.map((date: string) => ({
              date: new Date(date),
              type: 'dot',
              color: '#00c97d'
            })))
          }, [])
          hotelDispo.value = dispoArray
        } catch (error: unknown) {
          logError(error)
          hotelDispo.value = null
        }
      } else {
        hotelDispo.value = null
      }
    }

  })

  // Funciones
  const initDestination = async (currentRoute: { query: LocationQueryRaw }) => {
    try {
      if (currentRoute.query?.['data-destinationId']) {
        updateDestination(currentRoute)
      } else if (!cookieDestination.value) { // si esta set destinationId en url, obtener info destino con id
        const miIp = await $fetch<string>('https://api.ipify.org', {
          server: false,
          timeout: 2000,
        } as never)
        if (miIp) {
          const geoPath = '/api-web/geoip' as const
          const geoIpData = await $fetch<GeoIp>(geoPath, {
            method: 'post',
            body: { miIp },
            server: false,
          } as never)
          if (geoIpData) {
            await getDestinationGeoip(geoIpData)
          }
        }
      }
    } catch (error: unknown) {
      console.error('Error fetching GeoIP data:', getErrorMessage(error))
    }
  }

  /**
   * Resuelve un destino BYHOURS a partir de coordenadas (misma API que GeoIP).
   * Útil tras «Ubicación actual»: rellena ciudad/nombre e IDs en lugar de solo la etiqueta i18n.
   * `gpsLocationString` se guarda en `location` para mantener el punto GPS en búsqueda (cookie destination; no en URL).
   */
  const resolveDestinationByCoordinates = async (
    latitude: number,
    longitude: number,
    gpsLocationString: string
  ): Promise<Destination | null> => {
    try {
      const response = await $fetch<{ data?: Destination[] }>(
        `${runtimeConfig.public.API_CLIENT_BASE_URL}/destination`,
        {
          headers: { 'Content-Type': 'application/json' },
          query: {
            latitude,
            longitude,
            locale: locale.value
          },
          ...({ server: false } as Record<string, unknown>)
        }
      )
      const destinationInfo = response?.data?.[0]
      if (!destinationInfo) return null

      const country = destinationInfo.country_id
      if (country) {
        currencyStore.updateCurrencyByCountry(country)
      }

      const cleaned = removeCookieFileds(destinationInfo)
      cleaned.location = gpsLocationString.trim()
      return cleaned
    } catch (error: unknown) {
      console.error('Error resolving destination by coordinates:', getErrorMessage(error))
      return null
    }
  }

  const getDestinationGeoip = async (geoIpData: GeoIp) => { // obtener info destino con lat y lon
    const latitude = geoIpData?.location?.latitude
    const longitude = geoIpData?.location?.longitude
    const geoLat = parseGeoCoordinate(latitude)
    const geoLon = parseGeoCoordinate(longitude)

    if (geoLat !== null && geoLon !== null && (geoLat !== 0 || geoLon !== 0)) {
      geoIpLocation.value = { latitude: geoLat, longitude: geoLon }
    }

    try {
      const responseData = await $fetch<{ data?: Destination[] }>(
        `${runtimeConfig.public.API_CLIENT_BASE_URL}/destination`,
        {
          headers: { "Content-Type": "application/json" },
          query: {
            latitude,
            longitude,
            locale: locale.value
          }
        }
      )
      const destinationInfo = responseData?.data?.[0]

      // Moneda
      const country = destinationInfo?.country_id // código país
      if (country) {
        currencyStore.updateCurrencyByCountry(country)
      }

      destinationDisplayMode.value = 'named'
      selectedDestination.value = destinationInfo ?? null
      geoIpDestination.value = destinationInfo ?? null
    } catch (error: unknown) {
      console.error('Error fetching destination from GeoIP:', getErrorMessage(error))
    }
  }

  const updateDestination = async (currentRoute: { query: LocationQueryRaw }) => { // actualizar destino a partir de route o si es hotel
    if (selectedDestination.value?.category_id === 1 || currentRoute.query?.['data-destinationId']) {
      let idDestino = null

      if (currentRoute.query?.['data-destinationId']) { // si esta set destinationId en url, obtener info destino con id
        idDestino = currentRoute.query?.['data-destinationId'] ?? null
      } else if (selectedDestination.value?.category_id === 1) { // si es hotel, obtener info destino con id
        idDestino = selectedDestination.value?.destination_id ?? null
      }

      if (Number(idDestino) != selectedDestination.value?.element_id) {
        try {
          const destinationData = await $fetch<DestinationIdApiResponse>(`${useBaseUrl()}/destination-id`, {
            headers: { "Content-Type": "application/json" },
            query: {
              destination_id: idDestino,
              locale: locale.value
            }
          })
          if (destinationData?.data) {
            destinationDisplayMode.value = 'named'
            selectedDestination.value = removeCookieFileds(destinationData.data)
          }
        } catch (res: unknown) {
          logError(res)
        }
      }
    }
  }

  const handleDestinationRedirect = (destination: Destination, changed: boolean) => {
    const selectedDiaStore = useSelectedDayStore()
    const { selectedDay } = storeToRefs(selectedDiaStore)
    const searchModalsStore = useSearchModalsStore()
    const activeDateRangeMode = resolveActiveDateRangeMode(searchModalsStore.dateRangeMode, route.query)

    const query = {
      'data-destinationId': destination?.category_id === 3 ? null : destination?.category_id ? destination?.category_id === 1 ? destination?.destination_id : destination?.element_id : undefined, // undefined para no añadir a la url el data-destinationId
      'data-distance': destination?.search_distance,
      'data-search': destination?.name_translated,
      'data-country': destination?.country_name_translated,
      'search': destination?.name_translated,
      'data-checkinDate': selectedDay.value ? formatDateGuion(selectedDay.value) : '',
      'GFakeDate': selectedDay.value ? formatFakeDate(selectedDay.value) : '',
      'curr': currency.value,
      ...dateRangeModeQueryFields(activeDateRangeMode),
    }

    if (destinationDisplayMode.value === 'map_point' && route.path.includes('search')) {
      return
    }

    if (destination?.category_id === 1) { // redireccionar hotel
      if (changed) {
        redirectHotel(destination, query)
      }
    } else if (changed) { // redireccionar search
      navigateTo({
        path: `/${locale.value}/search`,
        query,
      } as RouteLocationRaw)
    }
  }

  const redirectHotel = async (newUbicacion: Destination, query: LocationQueryRaw) => {
    const regexHotel = /(?<=\/[a-z]{2}\/)[^/]+(?=\.html)/
    const matchHotel = newUbicacion?.['url']?.match(regexHotel)
    const slug = matchHotel ? matchHotel[0] : ''
    const path = `/${locale.value}/${slug}.html`

    navigateTo({
      path,
      query,
    } as RouteLocationRaw)
  }

  // Función para obtener la ubicación del navegador
  const getLocation = (callback: (result: { err?: string; data?: string }) => void) => {
    const n = typeof navigator !== 'undefined' ? (navigator as unknown as NavigatorWithGeo) : undefined
    if (!n?.geolocation) {
      callback({ err: t('errors.ubicacion.support') })
      return
    }

    n.permissions?.query({ name: 'geolocation' }).then((result: { state: string }) => {
      if (result.state === 'denied') {
        callback({ err: t('errors.ubicacion.block') })
        return
      }

      n.geolocation!.getCurrentPosition(
        (position: GeoLikePosition) => {
          callback({ data: `${position.coords.latitude}, ${position.coords.longitude}` })
        },
        (err: GeoLikeError) => {
          switch (err.code) {
            case 1:
              callback({ err: t('errors.ubicacion.permission') })
              break;
            case 2:
              callback({ err: t('errors.ubicacion.gps') })
              break;
            case 3:
              callback({ err: t('errors.ubicacion.timeout') })
              break;
            default:
              callback({ err: t('errors.ubicacion.error') })
          }
        }
      )
    })
  }

  return {
    selectedDestination,
    cookieDestination,
    geoIpDestination,
    geoIpLocation,
    labelDestination,
    labelDestinationDisplay,
    destinationDisplayMode,
    destination_id,
    distance,
    latitude,
    longitude,
    initDestination,
    updateDestination,
    handleDestinationRedirect,
    getDestinationGeoip,
    getLocation,
    resolveDestinationByCoordinates,
    hotelDispo
  }
})